'use client';

import { cn } from '@/shared/cn';
import { ChevronDown } from 'lucide-react';
import { useState } from 'react';

type AuthSelectOption = {
  label: string;
  value: string;
};

type AuthSelectBoxProps = {
  'aria-label': string;
  className?: string;
  onValueChange: (value: string) => void;
  options: AuthSelectOption[];
  placeholder?: string;
  value: string;
};

export function AuthSelectBox({ 'aria-label': ariaLabel, className, onValueChange, options, placeholder = '선택', value }: AuthSelectBoxProps) {
  const [isOpen, setIsOpen] = useState(false);
  const selectedOption = options.find(option => option.value === value);

  return (
    <div className="relative min-w-0">
      <button
        type="button"
        aria-label={ariaLabel}
        aria-expanded={isOpen}
        aria-haspopup="listbox"
        onClick={() => setIsOpen(open => !open)}
        className={cn(className, 'relative cursor-pointer pr-11 text-left')}>
        <span className={cn(!selectedOption && 'text-[#9E9E9E]')}>{selectedOption?.label ?? placeholder}</span>
        <span className="absolute top-1/2 right-1 flex size-10 -translate-y-1/2 items-center justify-center bg-white text-slate-400">
          <ChevronDown className={cn('size-5 transition-transform', isOpen && 'rotate-180')} aria-hidden="true" />
        </span>
      </button>

      {isOpen ? (
        <div
          role="listbox"
          aria-label={ariaLabel}
          className="absolute top-[calc(100%+4px)] right-0 left-0 z-30 max-h-60 overflow-y-auto rounded border border-slate-200 bg-white shadow-lg">
          {options.map(option => (
            <button
              key={option.value}
              type="button"
              role="option"
              aria-selected={option.value === value}
              onClick={() => {
                onValueChange(option.value);
                setIsOpen(false);
              }}
              className={cn(
                'block w-full cursor-pointer border-none px-4 py-2.5 text-left text-sm text-slate-600 hover:bg-slate-50',
                option.value === value ? 'bg-slate-50' : 'bg-white'
              )}>
              {option.label}
            </button>
          ))}
        </div>
      ) : null}
    </div>
  );
}
